import { isNil } from './object'

class PanoramaAssertionError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'PanoramaAssertionError'
  }
}

export function throwIfNil<T>(value: T | undefined | null, message: string): asserts value is T {
  if (isNil(value)) throw new PanoramaAssertionError(message)
}

/**
 * Safely extracts a human-readable message from an unknown thrown value.
 * `catch` bindings and rejection reasons are `unknown`; use this instead of
 * asserting `any` to read `.message`.
 */
export function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * Wordings each browser engine uses when a dynamic `import()` fails. A deploy replaces the
 * hashed assets in S3, so a tab that loaded before the deploy asks for chunk URLs that no
 * longer exist and receives index.html instead. Chromium and Gecko say so in a TypeError that
 * mentions the module; WebKit complains about the MIME type of the HTML it received; Vite's
 * preload helper throws a plain Error for the chunk's stylesheet; and a chunk that did load
 * can import a newer chunk whose exports were renamed, or resolve a lazy component to nothing.
 */
const dynamicImportFailureMessages = [
  'dynamically imported module',
  'Importing a module script failed',
  'is not a valid JavaScript MIME type',
  'Unable to preload CSS',
  'does not provide an export named',
  'Lazy element type must resolve to a class or function'
]

/**
 * Decides whether an error means the deployed bundle moved on without this tab. The caller
 * should offer a refresh rather than a crash screen, and error reporting should drop it.
 */
export const isDynamicImportError = (error: unknown) =>
  error instanceof Error &&
  dynamicImportFailureMessages.some((message) => error.message.includes(message))

/**
 * Messages the browser's fetch implementation gives a request that never reached the server:
 * offline, DNS failure, connection reset, or a navigation aborting the request. Each engine has
 * its own wording. They describe the user's connection rather than a defect in the app.
 */
export const fetchNetworkFailureMessages = [
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.'
]
